// The orb's ORIENT row (ATLAS): two full sentences, nothing else.
//   1. Where am I trying to go, and why?
//   2. What needs to be done to know I've arrived?
// Each is one text field; an empty field shows its question as the placeholder.
// Saved on blur into ATLAS (user_settings.oracle_spine → atlas.orient), per
// sector and per stage period (orientKey).
import React, { useEffect, useState } from 'react';
import { Text, TextInput, View } from 'react-native';
import { supabase } from '../../lib/supabase';
import type { Sector } from '../../domain/olmps';
import { saveOrientSentences, type OrientSentences } from '../../olmps/atlas-data';
import { useV10, DISPLAY_FONT } from '../theme';
import { useK } from './ui';

export const ORIENT_Q = { doing: 'Where am I trying to go, and why?', done: 'What needs to be done to know I’ve arrived?' } as const;

export function OrientFields({
  userId,
  sector,
  orientKey,
  value,
  onChange,
}: {
  userId: string | undefined;
  sector: Sector | null;
  orientKey: string;
  value: OrientSentences;
  onChange: (v: OrientSentences) => void;
}) {
  const t = useV10();
  const k = useK();
  const [draft, setDraft] = useState<OrientSentences>(value);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => setDraft(value), [value.doing, value.done, orientKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const commit = () => {
    if (draft.doing === value.doing && draft.done === value.done) return;
    onChange(draft);
    if (!userId || !sector) return;
    setErr(null);
    saveOrientSentences(supabase, userId, sector, orientKey, draft).catch((e) => setErr(e instanceof Error ? e.message : 'Not saved'));
  };

  const field = (key: keyof OrientSentences) => (
    <TextInput
      value={draft[key]}
      onChangeText={(v) => setDraft((d) => ({ ...d, [key]: v }))}
      onBlur={commit}
      placeholder={ORIENT_Q[key]}
      placeholderTextColor={t.ivoryFaint}
      editable={!!sector}
      multiline
      maxLength={300}
      style={[
        {
          alignSelf: 'stretch',
          minHeight: 44 * k,
          paddingHorizontal: 10 * k,
          paddingVertical: 8 * k,
          borderRadius: 10 * k,
          borderWidth: 1,
          borderColor: 'rgba(214,180,106,0.35)',
          color: t.ivory,
          fontSize: 13 * k,
          lineHeight: 18 * k,
          textAlignVertical: 'top',
        },
        { outlineStyle: 'none' } as object,
      ]}
    />
  );

  return (
    <View style={{ alignSelf: 'stretch', gap: 8 * k }}>
      {field('doing')}
      {field('done')}
      {!sector ? (
        <Text style={{ color: t.ivoryFaint, fontFamily: DISPLAY_FONT, fontStyle: 'italic', fontSize: 10.5 * k }}>Pick a sector first.</Text>
      ) : null}
      {err ? <Text style={{ color: t.lapsed, fontSize: 10.5 * k }}>{err}</Text> : null}
    </View>
  );
}
